import React, { useMemo } from 'react';
import { MessageCircle, X, Minus, Plus, ChevronUp, ChevronDown, Maximize2, Minimize2, Building2, User, Users, Hash, Globe, Shield, UserCheck, SlidersHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuCheckboxItem } from '@/components/ui/dropdown-menu';
import { UserAvatar } from '@/components/ui/user-avatar';
import { CompanyAvatar } from '@/components/ui/company-avatar';
import { resolveConversationAvatar, getCompanyAvatarProps } from '@/utils/chatAvatars';
import { useFloatingChat } from '@/hooks/useFloatingChat';
import { useUserAvatar } from '@/hooks/useUserAvatar';
import { ConversationItem } from './ConversationItem';
import { UnifiedChatWindow } from './UnifiedChatWindow';
import { ChatContainer } from './ChatContainer';
import { ChatMinimized } from './ChatMinimized';
import { cn } from '@/lib/utils';
import { LiveBadge } from '@/components/ui/live-badge';
import { useIsMobile } from '@/hooks/use-mobile';
import { getChatColorClasses } from '@/utils/chatColors';
import { useTranslation } from 'react-i18next';
import { useOptionalInterviewContext } from '@/contexts/InterviewContext';

export function FloatingChatList() {
  const isMobile = useIsMobile();
  const { t } = useTranslation('ui');
  
  // Check if interview is active - hide chat completely during interviews
  const interviewContext = useOptionalInterviewContext();
  const isInterviewActive = interviewContext?.isInterviewActive ?? false;
  
  // Use the new unified floating chat hook that handles all state and logic
  const {
    // UI State
    isExpanded,
    activeChatStates,
    
    // Data
    conversations,
    messages,
    loading,
    totalUnreadCount,
    userProfile,
    
    // Computed state
    filteredConversations,
    filteredData,
    
    // Actions
    setExpanded,
    openChatWindow,
    closeChatWindow,
    minimizeChatWindow,
    maximizeChatWindow,
    expandChatWindow,
    collapseChatWindow,
    
    // Filter management
    activeRoleFilters,
    activeTypeFilters,
    toggleRoleFilter,
    toggleTypeFilter,
    
    // Group management
    expandedGroups,
    toggleGroupExpansion,
    
    // Role data
    userRoles,
    conversationUserRoles,
    resetFilters,
    
    // Not using ephemeral conversations anymore
    
    // Chat operations
    sendMessage,
    markAsRead,
    handleTyping,
    subscribeToConversation,
    fetchMessages,
    getTypingUsers,
    
    // Helpers
    formatTime,
    getInitials,
    isConversationOnline,
    getFilterButtonText
  } = useFloatingChat();
  
  const {
    displayName,
    email,
    avatarUrl
  } = useUserAvatar();

  // All filtering logic and grouping is now handled by useFloatingChat hook

  // Filter management functions are now provided by useFloatingChat hook
  const getActiveFilterCount = () => {
    const roleCount = activeRoleFilters.has('all') ? 0 : activeRoleFilters.size;
    const typeCount = activeTypeFilters.has('all') ? 0 : activeTypeFilters.size;
    return roleCount + typeCount;
  };

  // All presence tracking, event listeners, and helper functions are now handled by useFloatingChat hook

  // All chat window management functions are now provided by useFloatingChat hook
  const restoreChatWindow = (conversationId: string) => {
    console.log('[FloatingChatList] Restoring minimized chat:', conversationId);
    
    // Ensure data and realtime are ready before revealing the window
    subscribeToConversation(conversationId);
    fetchMessages(conversationId);
    
    // Now maximize the window
    maximizeChatWindow(conversationId);
    
    // Mark messages as read when restoring chat
    if (markAsRead) {
      markAsRead(conversationId);
    }
  };
  
  const handleSendMessage = async (conversationId: string, message: string) => {
    if (!sendMessage) return;
    await sendMessage(conversationId, message);
  };
  
  const handleMarkAsRead = async (conversationId: string) => {
    if (!markAsRead) return;
    await markAsRead(conversationId);
  };

  // Memoize active chat windows to prevent unnecessary re-renders
  const activeChatWindows = useMemo(() => {
    // Get ordered chat entries based on the order array
    const orderedEntries = activeChatStates.order.map(conversationId => [conversationId, activeChatStates.states[conversationId]] as [string, any]).filter(([_, state]) => state); // Filter out any undefined states

    return orderedEntries.map(([conversationId, chatState], index) => {
      // Only use main conversations array as we're no longer using ephemeral conversations
      const conversation = conversations.find(c => c.id === conversationId);
      if (!conversation) {
        console.debug('[CHAT_LIST] No conversation found for ID:', conversationId);
        return null;
      }
      const conversationMessages = messages.get ? (messages.get(conversationId) || []) : [];

      // Calculate previous chat states for positioning with fixed widths
      const previousChatStates = orderedEntries.slice(0, index).map(([prevId, state]) => {
        return {
          isMinimized: state.isMinimized,
          isExpanded: state.isExpanded,
        };
      });
      const typingUsersInConversation = getTypingUsers ? getTypingUsers(conversationId) : [];
      const commonProps = {
        onSendMessage: (message: string) => handleSendMessage(conversationId, message),
        onTyping: handleTyping,
        onMinimize: () => minimizeChatWindow(conversationId),
        onExpand: () => expandChatWindow(conversationId),
        onCollapse: () => collapseChatWindow(conversationId),
        onClose: () => closeChatWindow(conversationId),
        isTyping: typingUsersInConversation.length > 0,
        typingUsers: typingUsersInConversation.map((t: any) => t.user_name),
        isExpanded: chatState.isExpanded
      };
      // Compute userRole for this conversation
      const conversationRoles = conversationUserRoles[conversation.id];
      let userRole: 'candidate' | 'hirer' = 'hirer'; // default
      if (conversationRoles && conversationRoles.includes('candidate')) {
        userRole = 'candidate';
      } else if ((conversation as any).isCandidate === true) {
        userRole = 'candidate';
      }
      
      const chatWindow = <UnifiedChatWindow 
        conversation={conversation} 
        messages={conversationMessages.map((msg: any) => ({
          ...msg,
          messageType: msg.message_type === 'system' || msg.message_type === 'call' ? 'text' : msg.message_type as 'text' | 'file' | 'image'
        }))} 
        isOnline={isConversationOnline(conversation)} 
        currentUserId={userProfile?.id} 
        userRole={userRole}
        {...commonProps} 
      />;
      if (chatState.isMinimized) {
        // Skip rendering minimized chats on mobile - only show on desktop
        if (isMobile) {
          return null;
        }
        
        // Map OptimizedConversation to BusinessChatConversation format
        const businessConversation: any = {
          ...conversation,
          type: userRole === 'candidate' ? 'talent' : 'business',
          conversationCategory: conversation.conversation_category,
          name: conversation.title,
          unreadCount: conversation.unread_count || 0,
          status: 'active',
          updatedAt: conversation.updated_at,
          originalId: conversation.id
        };
        
        return <ChatContainer key={conversationId} chatIndex={index} isMinimized={true} isExpanded={false} isChatListExpanded={isExpanded} previousChatStates={previousChatStates}>
          <ChatMinimized 
            conversation={businessConversation}
            onRestore={() => restoreChatWindow(conversationId)}
          />
        </ChatContainer>;
      }
      return <ChatContainer key={conversationId} chatIndex={index} isMinimized={false} isExpanded={chatState.isExpanded} isChatListExpanded={isExpanded} previousChatStates={previousChatStates}>
        {chatWindow}
      </ChatContainer>;
    });
  }, [activeChatStates, conversations, messages, isExpanded]);
  
  // Early exit if interview is active - completely hide chat UI
  if (isInterviewActive) {
    return null;
  }
  
  return <>
      {/* Chat Windows - Show on both mobile and desktop, ChatContainer handles mobile full-screen */}
      {activeChatWindows}
      
      {/* Collapsed Chat List - Desktop Only (hidden on mobile) */}
      {!isMobile && (
        <div className={cn(
          "fixed transition-all bottom-0 right-0 z-50 p-3",
          isExpanded ? "opacity-0 pointer-events-none absolute -z-10" : "opacity-100"
        )} style={{
          zIndex: 50
        }}>
          <Button 
            id="chat-list-collapsed" 
            onClick={() => setExpanded(true)} 
            className={cn(
              "rounded-full shadow-md hover:shadow-lg transition-all duration-300",
              "bg-card border border-border hover:bg-accent/20 hover:border-accent/50",
              "hover:scale-105 backdrop-blur-md flex items-center gap-2 animate-scale-in",
              "text-card-foreground relative h-12 px-4 text-sm"
            )} 
            variant="ghost"
            aria-label={totalUnreadCount > 0 
              ? `${t('chat.title')} - ${totalUnreadCount} ${t('chat.unread_messages')}` 
              : t('chat.title')
            }
          >
            <UserAvatar 
              src={avatarUrl} 
              name={displayName} 
              email={email} 
              size="sm" 
              className="w-7 h-7" 
            />
            
            <span className="text-sm font-medium">{t('chat.title')}</span>
            
            {totalUnreadCount > 0 && (
              <Badge 
                variant="unread" 
                className="text-xs animate-none min-w-5 h-5 px-1.5 flex items-center justify-center"
              >
                {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
              </Badge>
            )}
            
            <ChevronUp className="w-3.5 h-3.5" />
          </Button>
        </div>
      )}
      
      {/* Expanded Chat List - Mobile Full Screen or Desktop Floating */}
      <div className={cn(
        "fixed transition-all", 
        isExpanded ? "opacity-100 z-[100]" : "opacity-0 pointer-events-none -z-10",
        // Mobile: Full screen covering everything, Desktop: Standard positioning
        isMobile ? "inset-0 p-0" : "bottom-0 right-0 p-3"
      )}
        style={{
          zIndex: isExpanded ? 100 : -10
        }}
      >
        <div 
          id="chat-list-expanded" 
          className={cn(
            "relative overflow-hidden flex flex-col border border-border bg-card shadow-xl transition-colors duration-300 text-[13px] leading-5",
            // Mobile: Full screen with no rounding, Desktop: Sized and rounded
            isMobile 
              ? "w-full h-full rounded-none text-base" 
              : "w-80 md:w-96 h-[82vh] rounded-3xl text-sm"
          )}
        >
          {/* Header - Mobile Optimized */}
          <div className={cn(
            "border-b border-border bg-card-foreground/5",
            isMobile ? "px-4 pt-4 pb-3" : "px-3 pt-3 pb-2"
          )}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3 flex-1">
                {/* Enhanced Profile Frame */}
                <div className="relative">
                  <UserAvatar 
                    src={userProfile?.avatar_url || avatarUrl} 
                    name={userProfile?.name || displayName} 
                    email={email} 
                    size={isMobile ? "lg" : "md"} 
                    className="ring-2 ring-gradient-to-r from-primary/30 to-accent/30 shadow-lg" 
                  />
                  <div className={cn(
                    "absolute bg-green-500 border-2 border-card rounded-full animate-pulse",
                    isMobile ? "-bottom-1 -right-1 w-5 h-5" : "-bottom-0.5 -right-0.5 w-4 h-4"
                  )}></div>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className={cn(
                    "font-semibold text-foreground truncate",
                    isMobile ? "text-lg" : "text-sm"
                  )}>
                    {t('chat.title')}
                  </h3>
                  {(filteredData.conversations.length + filteredData.groups.length) > 0 && (
                    <p className={cn(
                      "text-muted-foreground truncate",
                      isMobile ? "text-sm" : "text-xs"
                    )}>
                      {filteredData.conversations.length + filteredData.groups.length} {(filteredData.conversations.length + filteredData.groups.length) > 1 ? t('chat.conversations') : t('chat.conversation')}
                    </p>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-1">
                {/* Compact Filter Button in Header */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size={isMobile ? "default" : "sm"} 
                      className={cn(
                        "relative transition-all duration-200",
                        isMobile ? "h-11 w-11 p-0" : "h-8 w-8 p-0",
                        getActiveFilterCount() > 0 
                          ? "bg-primary/10 text-primary hover:bg-primary/20" 
                          : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                      )}
                    >
                      <SlidersHorizontal className={cn(isMobile ? "w-5 h-5" : "w-4 h-4")} />
                      {getActiveFilterCount() > 0 && (
                        <div className={cn(
                          "absolute bg-primary text-primary-foreground rounded-full flex items-center justify-center font-medium animate-scale-in",
                          isMobile ? "-top-1 -right-1 w-4 h-4 text-xs" : "-top-1 -right-1 w-3 h-3 text-[10px]"
                        )}>
                          {getActiveFilterCount()}
                        </div>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  
                  <DropdownMenuContent className="w-64 max-h-80 overflow-y-auto" align="end">
                    <DropdownMenuLabel className="text-xs text-muted-foreground">
                      {t('chat.quick_filters')}
                    </DropdownMenuLabel>
                    <DropdownMenuCheckboxItem checked={activeRoleFilters.has('all') && activeTypeFilters.has('all')} onCheckedChange={() => {
                    resetFilters();
                  }} onSelect={e => e.preventDefault()}>
                      <Users className="w-4 h-4 mr-2" />
                      {t('chat.all_conversations')}
                    </DropdownMenuCheckboxItem>
                    
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-xs text-muted-foreground">
                      {t('chat.by_participants')}
                    </DropdownMenuLabel>
                    
                    {userRoles.roles && userRoles.roles.includes('internal') && <DropdownMenuCheckboxItem checked={activeRoleFilters.has('internal')} onCheckedChange={() => toggleRoleFilter('internal')} onSelect={e => e.preventDefault()}>
                        <Shield className="w-4 h-4 mr-2 text-red-500" />
                        {t('chat.internal_team')}
                      </DropdownMenuCheckboxItem>}
                    
                    {userRoles.companies && userRoles.companies.map((company: any) => <DropdownMenuCheckboxItem key={company.id} checked={activeRoleFilters.has(`company-${company.id}`)} onCheckedChange={() => toggleRoleFilter(`company-${company.id}`)} onSelect={e => e.preventDefault()}>
                        <Building2 className="w-4 h-4 mr-2 text-purple-500" />
                        {company.name}
                      </DropdownMenuCheckboxItem>)}
                    
                    <DropdownMenuCheckboxItem checked={activeRoleFilters.has('talent')} onCheckedChange={() => toggleRoleFilter('talent')} onSelect={e => e.preventDefault()}>
                      <User className="w-4 h-4 mr-2 text-blue-500" />
                      {t('chat.talent')}
                    </DropdownMenuCheckboxItem>
                    
                    <DropdownMenuCheckboxItem checked={activeRoleFilters.has('recruiter')} onCheckedChange={() => toggleRoleFilter('recruiter')} onSelect={e => e.preventDefault()}>
                      <UserCheck className="w-4 h-4 mr-2 text-green-500" />
                      {t('chat.recruiters')}
                    </DropdownMenuCheckboxItem>
                    
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-xs text-muted-foreground">
                      {t('chat.by_category')}
                    </DropdownMenuLabel>
                    
                    <DropdownMenuCheckboxItem checked={activeTypeFilters.has('hiring')} onCheckedChange={() => toggleTypeFilter('hiring')} onSelect={e => e.preventDefault()}>
                      <UserCheck className="w-4 h-4 mr-2" />
                      {t('chat.hiring')}
                    </DropdownMenuCheckboxItem>
                    
                    <DropdownMenuCheckboxItem checked={activeTypeFilters.has('support')} onCheckedChange={() => toggleTypeFilter('support')} onSelect={e => e.preventDefault()}>
                      <Users className="w-4 h-4 mr-2" />
                      {t('chat.support')}
                    </DropdownMenuCheckboxItem>
                    
                    <DropdownMenuCheckboxItem checked={activeTypeFilters.has('general')} onCheckedChange={() => toggleTypeFilter('general')} onSelect={e => e.preventDefault()}>
                      <Hash className="w-4 h-4 mr-2" />
                      {t('chat.general')}
                    </DropdownMenuCheckboxItem>
                    
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-xs text-muted-foreground">
                      {t('chat.by_type')}
                    </DropdownMenuLabel>
                    
                    <DropdownMenuCheckboxItem checked={activeTypeFilters.has('direct')} onCheckedChange={() => toggleTypeFilter('direct')} onSelect={e => e.preventDefault()}>
                      <User className="w-4 h-4 mr-2" />
                      {t('chat.direct_messages')}
                    </DropdownMenuCheckboxItem>
                    
                    <DropdownMenuCheckboxItem checked={activeTypeFilters.has('group')} onCheckedChange={() => toggleTypeFilter('group')} onSelect={e => e.preventDefault()}>
                      <Users className="w-4 h-4 mr-2" />
                      {t('chat.group_chats')}
                    </DropdownMenuCheckboxItem>
                    
                    <DropdownMenuCheckboxItem checked={activeTypeFilters.has('channel')} onCheckedChange={() => toggleTypeFilter('channel')} onSelect={e => e.preventDefault()}>
                      <Hash className="w-4 h-4 mr-2" />
                      {t('chat.channels')}
                    </DropdownMenuCheckboxItem>
                    
                    <DropdownMenuCheckboxItem checked={activeTypeFilters.has('public')} onCheckedChange={() => toggleTypeFilter('public')} onSelect={e => e.preventDefault()}>
                      <Globe className="w-4 h-4 mr-2" />
                      {t('chat.public')}
                    </DropdownMenuCheckboxItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                
                <Button 
                  variant="ghost" 
                  size={isMobile ? "default" : "sm"} 
                  className={cn(
                    "hover:bg-accent/50 text-muted-foreground hover:text-foreground transition-colors duration-200",
                    isMobile ? "h-11 w-11 p-0 bg-accent/10" : "h-8 w-8 p-0"
                  )} 
                  onClick={() => setExpanded(false)}
                >
                  {isMobile ? <Minimize2 className="w-5 h-5" /> : <ChevronDown className="w-4 h-4" />}
                </Button>
              </div>
            </div>

                
          </div>

          {/* Content */}
          <div className="flex-1 bg-gradient-to-b from-card/50 to-card overflow-hidden py-1">
            {loading ? <div className="flex items-center justify-center h-full gap-2 p-4">
                <Skeleton variant="soft" shimmer className="w-3 h-3 rounded-full" />
                <span className="text-sm text-muted-foreground">Loading conversations...</span>
              </div> : (filteredData.conversations.length + filteredData.groups.length + filteredData.items.length) === 0 ? <div className="flex flex-col items-center justify-center h-full p-6 text-center">
                <MessageCircle className="w-12 h-12 text-muted-foreground/50 mb-3" />
                <p className="text-sm font-medium text-foreground mb-1">No conversations yet</p>
                <p className="text-xs text-muted-foreground">
                  Start a conversation to see it here
                </p>
              </div> : <ScrollArea className="h-full">
                <div className="">
                  {/* Unified, time-sorted feed of groups and conversations */}
                  {filteredData.items.map((item: any) => {
                    if (item.type === 'group') {
                      const group = item.group;
                      const isExpanded = expandedGroups.has(group.id);
                      return (
                        <div key={`group-${group.id}`} className={cn(
                          "mx-2 mb-2 rounded-lg border border-border/20 bg-card/50 backdrop-blur-sm overflow-hidden",
                          isMobile ? "mx-3 mb-3" : "mx-2 mb-2"
                        )}>
                          {/* Group Header */}
                          <div 
                            className={cn(
                              "flex items-center gap-3 cursor-pointer transition-all duration-200 bg-primary/5 hover:bg-primary/10",
                              isMobile ? "p-4" : "p-3",
                              isExpanded && "border-b border-border/20"
                            )}
                            onClick={() => toggleGroupExpansion(group.id)}
                          >
                            <div className="flex-shrink-0">
                              {group.company_logo_url ? (
                                <CompanyAvatar 
                                  src={group.company_logo_url}
                                  name={group.company_name}
                                  alt={`${group.company_name} logo`}
                                  size="md"
                                  className="border border-blue-500/20"
                                />
                              ) : (
                                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-500/10 border border-blue-500/20">
                                  <Building2 className="w-5 h-5 text-blue-600" />
                                </div>
                              )}
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2 mb-1">
                                <h4 className={cn(
                                  "font-semibold text-foreground truncate",
                                  isMobile ? "text-base" : "text-sm"
                                )}>
                                  {group.job_title}
                                </h4>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  {group.totalUnreadCount > 0 && (
                                    <Badge variant="unread" className="text-xs min-w-5 h-5 px-1.5">
                                      {group.totalUnreadCount > 99 ? '99+' : group.totalUnreadCount}
                                    </Badge>
                                  )}
                                  <span className="text-xs text-muted-foreground">
                                    {formatTime(group.mostRecentMessageAt || '')}
                                  </span>
                                </div>
                              </div>
                              <p className={cn(
                                "text-muted-foreground mb-1",
                                isMobile ? "text-sm" : "text-xs"
                              )}>
                                {group.company_name} • {group.applicantCount} candidate{group.applicantCount > 1 ? 's' : ''}
                              </p>
                              {group.mostRecentMessage && (
                                <p className={cn(
                                  "text-muted-foreground truncate",
                                  isMobile ? "text-sm" : "text-xs"
                                )}>
                                  {group.mostRecentMessage}
                                </p>
                              )}
                            </div>
                            
                            <div className="flex-shrink-0">
                              {isExpanded ? (
                                <ChevronUp className="w-4 h-4 text-muted-foreground" />
                              ) : (
                                <ChevronDown className="w-4 h-4 text-muted-foreground" />
                              )}
                            </div>
                          </div>
                          
                          {/* Expanded Conversations */}
                          {isExpanded && (
                            <div className="bg-background/30">
                              {group.conversations.map((conv: any, idx: number) => (
                                <div
                                  key={conv.id}
                                  className={cn(
                                    "flex items-center gap-3 transition-all duration-200 cursor-pointer hover:bg-accent/30",
                                    isMobile ? "p-4" : "p-3",
                                    idx < group.conversations.length - 1 && "border-b border-border/50"
                                  )}
                                  onClick={() => {
                                    const opened = openChatWindow(conv.id);
                                    if (opened && isMobile) {
                                      setExpanded(false);
                                    }
                                  }}
                                >
                                  <div className="flex-shrink-0">
                                    {(() => {
                                      // Use participant's profile avatar if available, otherwise fallback to conversation avatar
                                      const avatarUrl = (conv as any).profile_avatar_url || (conv as any).avatar_url;
                                      const name = (conv as any).display_name || conv.title;
                                      return (
                                        <Avatar className="w-8 h-8">
                                          <AvatarImage 
                                            src={avatarUrl}
                                            alt={name}
                                          />
                                          <AvatarFallback className={cn("text-[10px] font-medium", `${getChatColorClasses(conv).accent} ${getChatColorClasses(conv).foreground}`)}>
                                            {getInitials(name)}
                                          </AvatarFallback>
                                        </Avatar>
                                      );
                                    })()}
                                  </div>
                                  
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between gap-2 mb-1">
                                      <h5 className={cn(
                                        "font-medium text-foreground truncate",
                                        isMobile ? "text-sm" : "text-xs"
                                      )}>
                                        {(() => {
                                          const isApplicationChat = conv.conversation_type === 'application' || 
                                                                  conv.conversation_category === 'application' || 
                                                                  conv.conversation_category === 'hiring';
                                          const userRole = conversationUserRoles[conv.id];
                                           
                                            // For candidates viewing application chats, show only job title/company name
                                            if (userRole?.includes('candidate') && isApplicationChat) {
                                              return conv.job_title || conv.company_name || 'Job Application';
                                            }
                                           
                                           // For hirers viewing application chats, show candidate name
                                           if (!userRole?.includes('candidate') && isApplicationChat) {
                                             return conv.candidate_name || conv.title;
                                           }
                                          
                                          // For all other chats, show title
                                          return conv.title;
                                        })()}
                                      </h5>
                                      <div className="flex items-center gap-2 flex-shrink-0">
                                        {conv.unread_count > 0 && (
                                          <Badge variant="unread" className="text-xs min-w-4 h-4 px-1">
                                            {conv.unread_count > 99 ? '99+' : conv.unread_count}
                                          </Badge>
                                        )}
                                        <span className="text-xs text-muted-foreground">
                                          {formatTime(conv.last_message_at || '')}
                                        </span>
                                      </div>
                                    </div>
                                    {conv.last_message_preview && (
                                      <p className={cn(
                                        "text-muted-foreground truncate",
                                        isMobile ? "text-xs" : "text-[10px]"
                                      )}>
                                        {conv.last_message_preview}
                                      </p>
                                    )}
                                  </div>
                                  
                                  <div className="flex-shrink-0">
                                    {isConversationOnline(conv) && (
                                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    }
                    
                    // Individual conversation
                    const conversation = item.conversation;
                    return (
                      <div
                        key={conversation.id}
                        className={cn(
                          "flex items-center gap-3 transition-all duration-200 cursor-pointer hover:bg-accent/30",
                          isMobile ? "mx-3 mb-3 p-4" : "mx-2 mb-2 p-3",
                          "rounded-lg border border-border bg-card"
                        )}
                        onClick={() => {
                          const opened = openChatWindow(conversation.id);
                          if (opened && isMobile) {
                            setExpanded(false);
                          }
                        }}
                      >
                        <div className="flex-shrink-0">
                          {(() => {
                            // Use participant's profile avatar if available, otherwise fallback to conversation avatar
                            const avatarUrl = (conversation as any).profile_avatar_url || (conversation as any).avatar_url;
                            const name = (conversation as any).display_name || conversation.title;
                            return (
                              <Avatar className="w-10 h-10">
                                <AvatarImage src={avatarUrl} alt={name} />
                                <AvatarFallback className={cn("text-xs font-medium", `${getChatColorClasses(conversation).accent} ${getChatColorClasses(conversation).foreground}`)}>
                                  {getInitials(name)}
                                </AvatarFallback>
                              </Avatar>
                            );
                          })()}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <h4 className={cn(
                              "font-semibold text-foreground truncate",
                              isMobile ? "text-base" : "text-sm"
                            )}>
                              {(() => {
                                const isApplicationChat = conversation.conversation_type === 'application' || 
                                                        conversation.conversation_category === 'application' || 
                                                        conversation.conversation_category === 'hiring';
                                const userRole = conversationUserRoles[conversation.id];
                                 
                                  // For candidates viewing application chats, show only job title/company name (never their own name)
                                  if (userRole?.includes('candidate') && isApplicationChat) {
                                    return conversation.job_title || conversation.company_name || 'Job Application';
                                  }
                                 
                                  // Use the role-based display title from the database function
                                  if (isApplicationChat) {
                                    return conversation.title;
                                  }
                                 
                                 // For all other chats, show title
                                 return (conversation.title || '').replace(/^Chat with\s+/i, '');
                              })()}
                             </h4>
                             {conversation.displaySubtitle && (
                               <p className="text-xs text-muted-foreground truncate mb-1">
                                 {conversation.displaySubtitle}
                               </p>
                             )}
                             <div className="flex items-center gap-2 flex-shrink-0">
                              {conversation.unread_count > 0 && (
                                <Badge variant="unread" className="text-xs min-w-5 h-5 px-1.5">
                                  {conversation.unread_count > 99 ? '99+' : conversation.unread_count}
                                </Badge>
                              )}
                              <span className="text-xs text-muted-foreground">
                                {formatTime(conversation.last_message_at || '')}
                              </span>
                            </div>
                          </div>
                          
                          {/* Category and type indicators */}
                          {conversation.company_name && (
                            <div className="flex items-center gap-2 mb-1">
                              <span className={cn(
                                "text-muted-foreground truncate",
                                isMobile ? "text-sm" : "text-xs"
                              )}>
                                {conversation.company_name}
                              </span>
                            </div>
                          )}
                          
                          {conversation.last_message_preview && (
                            <p className={cn(
                              "text-muted-foreground truncate",
                              isMobile ? "text-sm" : "text-xs"
                            )}>
                              {conversation.last_message_preview}
                            </p>
                          )}
                        </div>
                        
                        <div className="flex-shrink-0 flex flex-col items-end gap-1">
                          {isConversationOnline(conversation) && (
                            <LiveBadge />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>}
          </div>
        </div>
      </div>
    </>;
}